import { useMemo } from "react";
import { Invoice, isBudgetUtilizationStatus } from "@/types";
import { useSettings } from "./useSettings";

/**
 * Hook to calculate budget-related values
 */
export function useBudget(invoices: Invoice[]) {
  const { settings } = useSettings();

  const earlyPaymentBudget = settings?.early_payment_budget || 100000;

  // Budget utilized = scheduled + completed early payments (both consume budget)
  const usedBudget = useMemo(() => {
    return invoices
      .filter((inv) => isBudgetUtilizationStatus(inv.status))
      .reduce((sum, inv) => sum + (inv.discounted_payment_amount || inv.payment_amount), 0);
  }, [invoices]);

  const availableBudget = Math.max(0, earlyPaymentBudget - usedBudget);

  const pendingInvoices = useMemo(() => {
    return invoices.filter(
      (inv) => inv.status === "pending" && inv.email_error?.includes("Insufficient budget")
    );
  }, [invoices]);

  const pendingTotal = useMemo(() => {
    return pendingInvoices.reduce(
      (sum, inv) => sum + (inv.discounted_payment_amount || inv.payment_amount),
      0
    );
  }, [pendingInvoices]);

  const potentialSavings = useMemo(() => {
    return pendingInvoices.reduce((sum, inv) => sum + (inv.discount_amount || 0), 0);
  }, [pendingInvoices]);

  return {
    earlyPaymentBudget,
    usedBudget,
    availableBudget,
    pendingInvoices,
    pendingTotal,
    potentialSavings,
  };
}
